import { useState } from 'react';
import axios from 'axios';
import { motion } from 'framer-motion';
import { toast } from 'sonner';
import { Sparkles, ArrowRight, CheckCircle2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { trackEvent } from '@/lib/analytics';
import { API } from '@/lib/api';


/**
 * 2-field express contact form — placed above the fold on the home.
 * Drastically reduces friction vs the full /contact form.
 */
export function QuickContactForm() {
  const [form, setForm] = useState({ name: '', phone: '' });
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.phone.trim()) {
      toast.error('Prénom et téléphone requis');
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/contact`, {
        name: form.name.trim(),
        email: `quick+${Date.now()}@cfsc.fr`,
        phone: form.phone.trim(),
        message: "[Formulaire express home] Demande d'essai gratuit — rappeler cette personne.",
      });
      trackEvent('contact_form_submit', { form: 'quick_home' });
      setDone(true);
      toast.success('Message envoyé, on te rappelle dans les 24h.', { duration: 5000 });
    } catch {
      toast.error("Erreur, essaie via /contact ou appelle le 06 84 28 40 87");
    } finally {
      setLoading(false);
    }
  };

  return (
    <section className="relative py-14 md:py-20 overflow-hidden" data-testid="quick-contact-section">
      <div className="absolute inset-0 bg-gradient-to-br from-dark via-primary-dark to-dark" />
      <div className="absolute inset-0 mesh-gradient opacity-30" />
      <div className="absolute inset-y-0 right-0 w-1/2 bg-primary/10 blur-[140px] pointer-events-none" />

      <div className="container-custom relative z-10">
        <div className="grid lg:grid-cols-[1fr_1fr] gap-10 lg:gap-16 items-center">
          {/* Left: pitch */}
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true, margin: '-10%' }}
            transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
          >
            <span className="inline-flex items-center gap-1.5 text-primary-glow font-semibold text-[10px] tracking-[0.3em] uppercase mb-4">
              <Sparkles className="h-3 w-3" /> Séance gratuite · Sans engagement
            </span>
            <h2 className="font-heading text-3xl sm:text-4xl md:text-5xl text-white leading-[1.05] mb-4">
              Prêt à tester ?{' '}
              <span className="text-gradient-primary italic font-display">
                On te rappelle.
              </span>
            </h2>
            <p className="text-white/70 text-sm md:text-base leading-relaxed mb-5 max-w-md">
              Laisse-nous ton prénom et ton numéro. On t'appelle dans les 24h pour caler ta séance d'essai gratuite avec Nicolas.
            </p>
            <ul className="space-y-2 text-white/80 text-sm">
              {[
                'Cours d\'essai 100% gratuit',
                'Pas besoin d\'expérience préalable',
                'Bilan d\'entrée offert avec le coach',
              ].map((it, i) => (
                <li key={i} className="flex items-center gap-2">
                  <CheckCircle2 className="h-4 w-4 text-primary-glow shrink-0" />
                  {it}
                </li>
              ))}
            </ul>
          </motion.div>

          {/* Right: form card */}
          <motion.div
            initial={{ opacity: 0, x: 30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true, margin: '-10%' }}
            transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
            className="relative rounded-2xl bg-white/5 backdrop-blur-md border border-white/15 p-6 sm:p-8 shadow-[0_30px_80px_-20px_rgba(0,0,0,0.5)]"
            data-testid="quick-contact-form-card"
          >
            {!done ? (
              <form onSubmit={submit} className="space-y-3" data-testid="quick-contact-form">
                <label className="block text-[11px] font-bold tracking-[0.22em] uppercase text-primary-glow mb-1">
                  Ton prénom
                </label>
                <input
                  type="text"
                  required
                  placeholder="ex: Nicolas"
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  data-testid="quick-input-name"
                  className="w-full px-4 py-3.5 bg-white/10 border border-white/20 rounded-xl text-white placeholder:text-white/40 text-sm focus:outline-none focus:ring-2 focus:ring-primary-glow/40 focus:border-primary-glow/60 transition-all"
                />
                <label className="block text-[11px] font-bold tracking-[0.22em] uppercase text-primary-glow mb-1 pt-2">
                  Ton téléphone
                </label>
                <input
                  type="tel"
                  required
                  placeholder="06 XX XX XX XX"
                  value={form.phone}
                  onChange={(e) => setForm({ ...form, phone: e.target.value })}
                  data-testid="quick-input-phone"
                  className="w-full px-4 py-3.5 bg-white/10 border border-white/20 rounded-xl text-white placeholder:text-white/40 text-sm focus:outline-none focus:ring-2 focus:ring-primary-glow/40 focus:border-primary-glow/60 transition-all"
                />
                <Button
                  type="submit"
                  disabled={loading}
                  data-testid="quick-contact-submit"
                  className="w-full mt-3 btn-shine bg-primary-glow hover:bg-primary-light text-dark font-heading tracking-wider text-sm py-6 rounded-xl group disabled:opacity-60"
                >
                  {loading ? 'ENVOI…' : 'JE RÉSERVE MON ESSAI GRATUIT'}
                  <ArrowRight className="ml-1 h-4 w-4 transition-transform group-hover:translate-x-1" />
                </Button>
                <p className="text-center text-[11px] text-white/50 pt-2">
                  Pas de spam · On te rappelle sous 24h · RGPD conforme
                </p>
              </form>
            ) : (
              <div className="text-center py-8" data-testid="quick-contact-success">
                <div className="w-14 h-14 mx-auto bg-primary-glow/20 rounded-full flex items-center justify-center mb-4">
                  <CheckCircle2 className="h-7 w-7 text-primary-glow" />
                </div>
                <h3 className="font-heading text-2xl text-white mb-2">Top, message reçu !</h3>
                <p className="text-white/70 text-sm mb-6">
                  On te rappelle dans les 24h pour caler ton essai gratuit.
                </p>
                <a
                  href="tel:+33684284087"
                  onClick={() => trackEvent('phone_click', { location: 'quick_form_success' })}
                  className="inline-flex items-center gap-2 text-primary-glow hover:text-white text-sm font-semibold transition-colors"
                >
                  Tu préfères nous appeler ? 06 84 28 40 87
                </a>
              </div>
            )}
          </motion.div>
        </div>
      </div>
    </section>
  );
}
